/** Більше-Менше «Дуель»: pure helpers for the split-screen duel. */

export type MetricKey = "pop" | "area" | "gdp";
export type Dir = "up" | "down";

export interface DuelRound {
  code: string;
  name: string;
  val?: number;
}

/** The server's public view (worker/games/bilshe toPublic). */
export interface DuelView {
  metric: { key: MetricKey; label: string; icon: string };
  rounds: DuelRound[];
  picks: Dir[];
  correct: number;
  total: number;
  winAt: number;
}

/** Choreography of one answer, ms from the moment the answer comes back. */
export const COUNT_DELAY_MS = 90;
export const COUNT_MS = 640;
export const VERDICT_MS = 740;
export const HOLD_MS = 1550;
export const SLIDE_MS = 480;

interface Scale {
  min: number;
  div: number;
  unit: string;
}

const SCALES: Record<MetricKey, Scale[]> = {
  pop: [
    { min: 1e9, div: 1e9, unit: "млрд" },
    { min: 1e6, div: 1e6, unit: "млн" },
    { min: 0, div: 1e3, unit: "тис" },
  ],
  area: [
    { min: 1e6, div: 1e6, unit: "млн км²" },
    { min: 1e3, div: 1e3, unit: "тис км²" },
    { min: 0, div: 1, unit: "км²" },
  ],
  gdp: [
    { min: 1e12, div: 1e12, unit: "трлн" },
    { min: 1e9, div: 1e9, unit: "млрд" },
    { min: 0, div: 1e6, unit: "млн" },
  ],
};

export const WORDS: Record<MetricKey, { caption: string; ask: string }> = {
  pop: { caption: "людей", ask: "має більше чи менше людей?" },
  area: { caption: "площа", ask: "має більшу чи меншу площу?" },
  gdp: { caption: "ВВП", ask: "має більший чи менший ВВП?" },
};

export interface Amount {
  prefix: string;
  num: string;
  unit: string;
}

/**
 * A value as the duel prints it: three significant digits in the unit that suits
 * the FINAL value, decimal comma. `shown` lets a count-up pass through smaller
 * numbers without the unit or the decimals changing under it. The chain keeps
 * neighbours ≥5% apart, so three digits never print a tie.
 */
export function amount(metric: MetricKey, final: number, shown = final): Amount {
  const scales = SCALES[metric];
  // 999.6 млн already prints as 1,00 млрд, not "1000 млн".
  const scale = scales.find((s) => final >= s.min * 0.9995) ?? (scales.at(-1) as Scale);
  const x = final / scale.div;
  const digits = x >= 99.95 ? 0 : x >= 9.995 ? 1 : 2;
  return { prefix: metric === "gdp" ? "$" : "", num: decimal(shown / scale.div, digits), unit: scale.unit };
}

export function amountText(metric: MetricKey, n: number): string {
  const a = amount(metric, n);
  return `${a.prefix}${a.num} ${a.unit}`;
}

function decimal(v: number, digits: number): string {
  const [int = "0", frac] = v.toFixed(digits).split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return frac ? `${grouped},${frac}` : grouped;
}

/** Was pick i right? null until both values are known. A tie counts as «менше», like the server. */
export function verdictAt(view: DuelView, i: number): boolean | null {
  const pick = view.picks[i];
  const a = view.rounds[i]?.val;
  const b = view.rounds[i + 1]?.val;
  if (pick === undefined || a === undefined || b === undefined) return null;
  return (pick === "up") === b > a;
}

/** What round i really was. */
export function truthAt(view: DuelView, i: number): "більше" | "менше" | null {
  const a = view.rounds[i]?.val;
  const b = view.rounds[i + 1]?.val;
  if (a === undefined || b === undefined) return null;
  return b > a ? "більше" : "менше";
}

export type Seg = "ok" | "bad" | "now" | "todo";

/** Progress as the stage shows it: the round on screen turns ok/bad only once its verdict is out. */
export function segments(view: DuelView, shown: number, verdictOn: boolean): Seg[] {
  return Array.from({ length: view.total }, (_, i) => {
    if (i < shown || (i === shown && verdictOn)) {
      const v = verdictAt(view, i);
      return v === null ? "todo" : v ? "ok" : "bad";
    }
    return i === shown ? "now" : "todo";
  });
}

export function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

/** A stable offset so neighbouring flags don't drift in lockstep. */
export function driftDelay(code: string): number {
  return ((code.charCodeAt(0) || 0) * 7 + (code.charCodeAt(1) || 0) * 13) % 16;
}
